import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { EASE } from "../../lib/motion";

// Parses values like "₹2.4Cr+", "97%", "1,250", "$12.5M", "x4.2" into prefix/number/suffix.
const parseValue = (raw) => {
    const s = String(raw ?? "").trim();
    const m = s.match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m) return null;
    const [, prefix, numStr, suffix] = m;
    const cleaned = numStr.replace(/,/g, "");
    const target = parseFloat(cleaned);
    if (!isFinite(target)) return null;
    const decPart = cleaned.split(".")[1] || "";
    const decimals = decPart.length;
    const hasGrouping = numStr.includes(",");
    return { prefix, suffix, target, decimals, hasGrouping };
};

const reducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const AnimatedNumber = ({ value, seconds = 1.6, className = "", testId }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });

    // Memoise so the effect doesn't restart on every render.
    const parsed = useMemo(() => parseValue(value), [value]);

    const target = parsed ? parsed.target : 0;
    const duration = useMemo(
        () => Math.max(0.3, Math.min(12, Number(seconds) || 1.6)),
        [seconds]
    );

    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!parsed) return;
        if (!inView) return;
        if (reducedMotion()) {
            setDisplay(target);
            return;
        }
        // Start fresh each time the effect fires (it will fire only when
        // inView / target / duration actually change — not on every render).
        setDisplay(0);
        const controls = animate(0, target, {
            duration,
            ease: EASE,
            onUpdate: (latest) => setDisplay(latest),
        });
        return () => controls.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, target, duration]);

    if (!parsed) {
        return (
            <span ref={ref} className={className} data-testid={testId}>
                {value}
            </span>
        );
    }

    const formatted = display.toLocaleString(undefined, {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
        useGrouping: parsed.hasGrouping,
    });

    return (
        <span ref={ref} className={className} data-testid={testId}>
            {parsed.prefix}{formatted}{parsed.suffix}
        </span>
    );
};
